import React, { forwardRef, useCallback, useState, type ComponentPropsWithoutRef } from 'react';
import Button, { IconButton } from '@atlaskit/button/new';
import type { NewCoreIconProps } from '@atlaskit/icon';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { cssMap, cx } from '@atlaskit/css';
import { JiraInlineDialog as InlineDialog } from '@atlassian/jira-inline-dialog/src/ui/jira-inline-dialog.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	ToolbarButtonBaseProps,
	ToolbarButtonModalProps,
	ToolbarButtonProps,
	ToolbarPopupButtonProps,
} from './types.tsx';
import { useCombinedRefs } from './utils.tsx';

const styles = cssMap({
	hoverWrapper: {
		padding: '0',
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface.overlay'),
		'&:hover': {
			backgroundColor: token('elevation.surface.overlay.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.overlay.pressed'),
		},
	},
	actionDisabledWrapper: {
		backgroundColor: token('elevation.surface.overlay'),
	},
	disabledButtonWrapper: {
		backgroundColor: token('elevation.surface.overlay'),
		transition: 'none',
		opacity: token('opacity.disabled'),
	},
});

const ButtonBase = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<typeof Button>>(
	(props, ref) => <Button appearance="subtle" {...props} ref={ref} />,
);

const IconButtonBase = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<typeof IconButton>>(
	(props, ref) => <IconButton appearance="subtle" {...props} ref={ref} />,
);

const ToolbarButtonBase = ({
	action,
	buttonProps,
	buttonRef,
	tabIndex,
}: ToolbarButtonBaseProps) => {
	const handleOnClick = useCallback(
		(
			e: React.MouseEvent<HTMLButtonElement>,
			analyticsEvent: UIAnalyticsEvent,
			buttonName: string,
			isDisabled: boolean,
		) => {
			fireUIAnalytics(analyticsEvent, 'bulkActionFloatingToolbar', {
				buttonName,
				isDisabled,
			});

			!action.isDisabled && 'onClick' in action && action.onClick?.(e, analyticsEvent);
			buttonProps?.onClick?.(e, analyticsEvent);
		},
		[action, buttonProps],
	);

	const getIconBefore = useCallback(
		(iconProps: NewCoreIconProps) => {
			if (!buttonProps?.isDisabled && action.isDisabled)
				return <WarningIcon {...iconProps} color={token('color.icon.warning')} />;
			return action.icon ? (
				<action.icon
					{...iconProps}
					color={
						expVal('sunsetting_bulk_edit', 'isEnabled', false)
							? token('color.icon')
							: token('color.icon.inverse')
					}
					primaryColor={
						expVal('sunsetting_bulk_edit', 'isEnabled', false)
							? token('color.icon')
							: token('color.icon.inverse')
					}
				/>
			) : undefined;
		},
		[action, buttonProps?.isDisabled],
	);

	const getButtonComponent = useCallback(() => {
		const actionText = (
			<Text
				{...(!expVal('sunsetting_bulk_edit', 'isEnabled', false) && {
					color: 'color.text.inverse',
				})}
			>
				{action.name}
			</Text>
		);

		if (action.iconOnly) {
			return (
				<IconButtonBase
					{...buttonProps}
					label={action.name}
					onClick={(...args) => {
						handleOnClick(...args, action.name, !!action.isDisabled);
					}}
					ref={buttonRef}
					tabIndex={tabIndex}
					isDisabled={buttonProps?.isDisabled}
					icon={(iconProps) =>
						action.icon ? (
							<action.icon
								{...iconProps}
								color={
									expVal('sunsetting_bulk_edit', 'isEnabled', false)
										? token('color.icon')
										: token('color.icon.inverse')
								}
								primaryColor={
									expVal('sunsetting_bulk_edit', 'isEnabled', false)
										? token('color.icon')
										: token('color.icon.inverse')
								}
							/>
						) : undefined
					}
					isTooltipDisabled={false}
				/>
			);
		}

		return (
			<ButtonBase
				{...buttonProps}
				onClick={(...args) => {
					handleOnClick(...args, action.name, !!action.isDisabled);
				}}
				ref={buttonRef}
				tabIndex={tabIndex}
				isDisabled={buttonProps?.isDisabled}
				iconBefore={getIconBefore}
				id={fg('deliveroo_a11y_enghealth_fixes_oct_25') ? buttonProps?.id : undefined}
			>
				{actionText}
			</ButtonBase>
		);
	}, [action, buttonProps, buttonRef, handleOnClick, tabIndex, getIconBefore]);

	if (expVal('sunsetting_bulk_edit', 'isEnabled', false)) {
		// We can combine the below Box and Button when safe override using XCSS is implemented
		// see: https://atlassian.design/components/button/button-new/examples#custom-buttons
		return (
			<Box
				testId="floating-toolbar.ui.toolbar-button.button"
				xcss={cx(
					styles.hoverWrapper,
					action.isDisabled && styles.actionDisabledWrapper,
					buttonProps?.isDisabled && styles.disabledButtonWrapper,
				)}
				ref={buttonProps?.ref}
			>
				{getButtonComponent()}
			</Box>
		);
	}

	return (
		// We can combine the below Box and Button when safe override using XCSS is implemented
		// see: https://atlassian.design/components/button/button-new/examples#custom-buttons
		<Box
			xcss={[
				hoverWrapperStyles,
				action.isDisabled && actionDisabledWrapperStyles,
				buttonProps?.isDisabled && disabledButtonWrapperStyles,
			]}
			ref={buttonProps?.ref}
		>
			{getButtonComponent()}
		</Box>
	);
};

const ToolbarPopupButton = ({
	action,
	isDisabled,
	buttonRef,
	tabIndex,
}: ToolbarPopupButtonProps) => {
	return (
		<Popup
			isOpen={action.isOpen}
			placement="auto"
			messageId="floating-toolbar.ui.toolbar-button.popup"
			messageType="transactional"
			onClose={action.onClose}
			trigger={(triggerProps) => (
				<ToolbarButtonBase
					action={action}
					buttonProps={{
						isDisabled,
						id: fg('deliveroo_a11y_enghealth_fixes_oct_25') ? action?.buttonId : undefined,
						...triggerProps,
					}}
					buttonRef={buttonRef}
					tabIndex={tabIndex}
				/>
			)}
			label={action.name}
			content={action.content}
			role={action.role}
		/>
	);
};

const ToolbarModalButton = forwardRef<HTMLElement, ToolbarButtonModalProps>(
	({ action, isDisabled, buttonRef, tabIndex }, ref) => {
		const combinedRefs = useCombinedRefs(buttonRef, ref);
		const lowerCaseActionName = action.name.toLowerCase();

		return (
			<ToolbarButtonBase
				action={action}
				buttonProps={{
					isDisabled,
					interactionName: `bulk-operations.${lowerCaseActionName}.modal`,
				}}
				buttonRef={combinedRefs}
				tabIndex={tabIndex}
			/>
		);
	},
);

const ToolbarDisabledButton = ({ action, buttonRef, tabIndex }: ToolbarButtonProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const toggleDialog = useCallback(() => {
		setIsOpen((oldState) => !oldState);
	}, [setIsOpen]);

	const onCloseDialog = useCallback(() => setIsOpen(false), [setIsOpen]);

	return (
		<InlineDialog
			messageId="floating-toolbar.ui.toolbar-button.inline-dialog.disabled-button"
			messageType="transactional"
			onClose={onCloseDialog}
			content={<Box role="alert">{action.disabledMessage}</Box>}
			isOpen={isOpen}
			placement="top-start"
			testId="floating-toolbar.ui.toolbar-button.disabled-button-with-popup-inline-dialog"
		>
			<ToolbarButtonBase
				action={action}
				buttonProps={{
					onClick: toggleDialog,
				}}
				buttonRef={buttonRef}
				tabIndex={tabIndex}
			/>
		</InlineDialog>
	);
};

export const ToolbarButton = ({
	action,
	isDisabled,
	buttonRef,
	tabIndex,
}: ToolbarButtonProps): React.JSX.Element => {
	if (!isDisabled && action.isDisabled) {
		return (
			<ToolbarDisabledButton
				action={action}
				isDisabled={isDisabled}
				buttonRef={buttonRef}
				tabIndex={tabIndex}
			/>
		);
	}

	if (action.type === 'popup') {
		return (
			<ToolbarPopupButton
				action={action}
				isDisabled={isDisabled}
				buttonRef={buttonRef}
				tabIndex={tabIndex}
			/>
		);
	}
	if (action.type === 'modal') {
		return (
			<ModalEntryPointPressableTrigger
				entryPoint={action.entryPoint}
				entryPointParams={action.entryPointParams}
				entryPointProps={action.entryPointProps}
				useInternalModal
				modalProps={action.modalProps}
				title={action.title}
			>
				{({ ref }) => (
					<ToolbarModalButton
						action={action}
						isDisabled={isDisabled}
						buttonRef={buttonRef}
						tabIndex={tabIndex}
						ref={ref}
					/>
				)}
			</ModalEntryPointPressableTrigger>
		);
	}

	return (
		<ToolbarButtonBase
			action={action}
			buttonProps={{ isDisabled }}
			buttonRef={buttonRef}
			tabIndex={tabIndex}
		/>
	);
};

const hoverWrapperStyles = xcss({
	padding: '0',
	borderRadius: 'radius.small',
	backgroundColor: 'color.background.neutral.bold.pressed',
	':hover': {
		backgroundColor: 'color.background.neutral.bold.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.bold',
	},
});

const actionDisabledWrapperStyles = xcss({
	':hover': {
		backgroundColor: 'color.background.inverse.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.inverse.subtle.pressed',
	},
});

const disabledButtonWrapperStyles = xcss({
	transition: 'none',
	opacity: '0.5',
	':hover': {
		backgroundColor: 'color.background.neutral.bold.pressed',
	},
	':active': {
		backgroundColor: 'color.background.neutral.bold.pressed',
	},
});
